import type { QuestionSchema } from '@common/types/templateFormFields';
import {
    formatIntakeFieldDisplayValuePreservingType,
    formatIntakeTableDisplayValue,
    getQuestionSchemaType,
    type QuestionSchemaInput,
} from '@common/utils/intakeFieldFormatting';
import type { IntakeResponseObj } from './types';

export function formatIntakeFieldDisplayValue(
    value: unknown,
    questionSchema?: QuestionSchemaInput,
): unknown {
    return formatIntakeFieldDisplayValuePreservingType(value, questionSchema);
}

export function formatIntakeResponseObjForDisplay(
    field: IntakeResponseObj,
    templateFieldsById?: Map<string, { questionSchema?: QuestionSchemaInput }>,
): IntakeResponseObj {
    if (field._fileId) {
        return field;
    }

    const templateField = templateFieldsById?.get(field.TemplateFormFieldId);

    if (getQuestionSchemaType(templateField?.questionSchema) === 'table') {
        const table = formatIntakeTableDisplayValue(field.Value, templateField?.questionSchema);
        return {
            ...field,
            // An empty Value lets the existing empty-answer filters hide unanswered tables
            Value: table.rows.length > 0 ? field.Value : '',
            _table: table,
        };
    }

    const displayValue = formatIntakeFieldDisplayValue(field.Value, templateField?.questionSchema);

    if (displayValue === field.Value) {
        return field;
    }

    return { ...field, Value: displayValue };
}

export function applyIntakeFieldDisplayFormatting(
    groupedData: Record<string, IntakeResponseObj[] | IntakeResponseObj[][]>,
    templateFieldsById?: Map<string, { questionSchema?: QuestionSchemaInput }>,
): Record<string, IntakeResponseObj[] | IntakeResponseObj[][]> {
    const formatted: Record<string, IntakeResponseObj[] | IntakeResponseObj[][]> = {};

    for (const [tab, fields] of Object.entries(groupedData)) {
        formatted[tab] = fields.map((field) => {
            if (Array.isArray(field)) {
                return field.map((nestedField) =>
                    formatIntakeResponseObjForDisplay(nestedField, templateFieldsById),
                );
            }

            return formatIntakeResponseObjForDisplay(field, templateFieldsById);
        }) as IntakeResponseObj[] | IntakeResponseObj[][];
    }

    return formatted;
}

export function buildTemplateFieldsById(
    templateFormFields: Array<{
        templateFormFieldId: string;
        questionSchema?: QuestionSchema | null;
    }>,
): Map<string, { questionSchema?: QuestionSchemaInput }> {
    return new Map(
        templateFormFields.map((field) => [
            field.templateFormFieldId,
            { questionSchema: field.questionSchema },
        ]),
    );
}
